// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from "react";
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { array, bool, func, object, string } from "prop-types";
import { Button, Grid, Link } from '@material-ui/core';
import { filter } from "lodash";

import ListContainer from '../../list';
import { Loading } from '../../../components';
import { FilterByText } from '../../../pageComponents'
import { getI18n } from "../../../utils/intl";
import {
  fetchAuthProvidersList,
  resetAuthProvidersListStatus,
} from '../../../actions/authProviders';
import {
  getAuthProvidersList,
  getAuthProvidersListError,
} from '../../../reducers/authProviders';
import {
  ROUTES,
} from '../../../constants/routes';

const labels = {
  nameHeader: 'label.auth.providers.list.name',
  issuerHeader: 'label.auth.providers.list.issuer',
};

import * as CONSTANTS from '../../../constants';

export const AuthProviders = (props) => {
  const {
    authProvidersList = [],
    classes,
    fetchAuthProvidersList,
    authProvidersListError,
    resetAuthProvidersListStatus,
    history,
    isLoading,
  } = props;
  const intl = getI18n(useIntl());
  const [filterName, setFilterName] = useState('');

  useEffect(() => {
    fetchAuthProvidersList();
    return resetAuthProvidersListStatus;
  }, []);

  useEffect(() => {
    if (authProvidersListError) {
      setNotificationStatus('error');
      setNotificationMessage(authProvidersListError);
    }
  }, [authProvidersListError]);

  const [notificationStatus, setNotificationStatus] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');

  const getListColumns = () => {
    return ([
      {
        id: 'name',
        label: intl.getI18nMessage(labels.nameHeader),
        value: ((row) => <Link underline="none" href={`${ROUTES.authProvidersList.path}/${row.uuid}/edit/${CONSTANTS.AUTH_PROVIDER_OVERVIEW_TAB_ID}`}>{row.name}</Link>),
        minWidth: 80,
      },
      {
        id: 'issuer',
        label: intl.getI18nMessage(labels.issuerHeader),
        minWidth: 80,
      },
    ]);
  };

  const emptyFunction = () => {};

  const onAddAuthProvider = () => {
    history.push(ROUTES.authProvidersAdd.path);
  };

  const renderFilterAndSortContent = () => (
    <Fragment>
      <Grid className={classes.controlsContainer} item md={3} sm={4} xs={6}>
        <FilterByText
          fieldContainerClass={classes.nameFieldContainer}
          handleBlur={emptyFunction}
          handleChange={setFilterName}
          id={'auth-provider-name-filter'}
          name={intl.getI18nMessage('label.filter')}
          onKeyPress={emptyFunction}
          placeholder={intl.getI18nMessage(labels.nameHeader)}
          value={filterName}
        />
      </Grid>
      <Grid item md={3} sm={4} xs={6}>
        <Button
          className={classes.addAuthProviderBtn}
          disabled={false}
          variant="contained" color="secondary"
          onClick={onAddAuthProvider}
          id={'auth-providers-add'}
          data-apim-test={'auth-providers-add'}
          data-layer7-test={'auth-providers-add'}
        >
          {intl.getI18nMessage('label.auth.providers.list.add.auth.provider')}
        </Button>
      </Grid>
    </Fragment>
  );

  const listColumns = getListColumns();
  const filteredRows = filter(authProvidersList, ({ name }) =>
    name && name.toLowerCase().includes(filterName && filterName.toLowerCase()));

  return(
    <div className={classes.details}>
      {isLoading && <Loading pageLoader={true} />}
      <ListContainer
        addButtonLabel={''}
        columns={listColumns}
        filterAndSortContent={renderFilterAndSortContent()}
        hasPaginationDisabled={true}
        hidePagination={true}
        isLoading={isLoading}
        listPageId="auth-providers-list"
        noResultsMessage={intl.getI18nMessage('label.request.list.no.results')}
        notificationId="category-templates-notifications"
        notificationMessage={notificationMessage}
        notificationStatus={notificationStatus}
        notifyMessages={() => {}}
        onAdd={emptyFunction}
        pageBodyClass={classes.authProvidersPageBodyClass}
        pageClass={classes.authProvidersPageClass}
        pageFilterAndSortClass={classes.authProvidersPageFilterAndSortClass}
        pageHeaderDescription={''}
        pageHeaderTitle={''}
        rows={filteredRows}
        setNotificationMessage={setNotificationMessage}
        setNotificationStatus={setNotificationStatus}
        showListHeader={false}
      />
    </div>
  );
};

AuthProviders.propTypes = {
  authProvidersList: array,
  authProvidersListError: string,
  classes: object,
  fetchAuthProvidersList: func,
  resetAuthProvidersListStatus: func,
  history: object,
  isLoading: bool,
};

const mapStateToProps = state => ({
  authProvidersList: getAuthProvidersList(state),
  authProvidersListError: getAuthProvidersListError(state),
});

const mapDispatchToProps = {
  fetchAuthProvidersList,
  resetAuthProvidersListStatus,
};

AuthProviders.displayName = 'AuthProviders';

export default (
  connect(mapStateToProps, mapDispatchToProps)
)(AuthProviders);
